import { MachineItem } from '../types/machine';
import { translate, I18n } from '@utils/i18n';
import {
  UserConfig,
  EMachineConfigUIViewType,
  MachineConfigUIMetricContent,
  MachineConfigUICatalog,
  UIMetric,
  MachineConfigUIMetricEnum,
  MachineConfigUIParameter,
  EUnorderedMetricEnum,
  MachineConfigUIProductionTypeEnum
} from '../types/config';
import { Option } from '../types/common';
import { METRICS_BLACKLIST } from '@constants/index';
import { FeatureFlagsType } from '@hooks/useFeatureFlags';
import { isMetricSupportedByFeatureFlags } from './metricUtils';

export const machineAsOption = (machine: MachineItem): Option => ({
  label: machine.title,
  value: machine.id.toString()
});

export const machinesAsOptions = (
  machines: MachineItem[],
  addAllMachinesOption = false
): Option[] => {
  const options = machines.map(machineAsOption);
  if (addAllMachinesOption) {
    options.unshift({
      label: translate('all_machines'),
      value: 'all-machines'
    });
  }
  return options;
};

export const isUnorderedMetric = <
  T extends { metric: MachineConfigUIMetricEnum }
>(
  metric: T
): metric is T & { metric: EUnorderedMetricEnum } => {
  return Object.values(EUnorderedMetricEnum).includes(
    metric.metric as EUnorderedMetricEnum
  );
};

export const getMachineMetrics = (
  config: UserConfig,
  machineId: number,
  viewType: EMachineConfigUIViewType
): MachineConfigUIMetricContent[] | undefined => {
  return config.machineMetricConfig[machineId][viewType].filter(
    (m) => m.enabled
  );
};

export const isMetricSupported = (
  metricName: MachineConfigUIMetricEnum,
  machineId: number,
  viewType: EMachineConfigUIViewType,
  machineMetricCatalog: MachineConfigUICatalog,
  featureFlags: FeatureFlagsType<boolean>
): boolean => {
  return (
    !!machineMetricCatalog[metricName]?.machines?.includes(machineId) &&
    !!machineMetricCatalog[metricName]?.viewType?.includes(viewType) &&
    isMetricSupportedByFeatureFlags(
      machineMetricCatalog[metricName],
      featureFlags
    ) &&
    !METRICS_BLACKLIST.includes(metricName)
  );
};

export const hasMetric = (
  availableMetrics: UIMetric[],
  metricName: MachineConfigUIMetricEnum
) => availableMetrics.some((m) => m.metric === metricName);

export function getMachineMetricsCatalog(
  machineId: number,
  viewType: EMachineConfigUIViewType,
  machineMetricCatalog: MachineConfigUICatalog,
  featureFlags: FeatureFlagsType<boolean>
): UIMetric[] {
  const data = Object.keys(machineMetricCatalog).reduce(
    (acc, metricName, index) => {
      if (
        isMetricSupported(
          metricName as MachineConfigUIMetricEnum, // this is safe
          machineId,
          viewType,
          machineMetricCatalog,
          featureFlags
        )
      ) {
        acc.push({
          metric: metricName as MachineConfigUIMetricEnum,
          order: index,
          enabled: false,
          parameters: machineMetricCatalog[metricName].parameters
        });
      }
      return acc;
    },
    [] as UIMetric[]
  );
  return data;
}

export const isMetricEnabled = (
  metrics: UIMetric[],
  metricName: MachineConfigUIMetricEnum
) => metrics.some((m) => m.metric === metricName && m.enabled);

export const getMachineMetricsConfig = (
  machineId: number,
  viewType: EMachineConfigUIViewType,
  machineMetricConfig: {
    [key: string]: {
      [key: string]: MachineConfigUIMetricContent[];
    };
  },
  machineMetricCatalog: MachineConfigUICatalog,
  featureFlags: FeatureFlagsType<boolean>,
  maxMetrics?: number
): UIMetric[] => {
  if (!machineMetricConfig[machineId]?.[viewType]) return [];
  const enabledMetrics = machineMetricConfig[machineId][viewType]
    .map((metric) => ({
      metric: metric.metric,
      order: metric.order,
      enabled: metric.enabled,
      parameters: metric.parameters
    }))
    .filter((m) =>
      isMetricSupportedByFeatureFlags(
        machineMetricCatalog[m.metric],
        featureFlags
      )
    )
    .filter((m) => m.enabled);
  if (maxMetrics === undefined) return enabledMetrics;
  // recall: non-orderable metrics don't count towards the maxMetrics limit
  const unorderableMetrics = enabledMetrics.filter((m) => isUnorderedMetric(m));
  const otherMetrics = enabledMetrics.filter((m) => !isUnorderedMetric(m));
  // always include unorderable metrics when enabled
  return [...otherMetrics.slice(0, maxMetrics), ...unorderableMetrics];
};

export type MetricGroupId =
  | 'time'
  | 'availability'
  | 'products'
  | 'performance'
  | 'quality'
  | 'oee'
  | 'other';

export type MetricOptionGroup = {
  label: string;
  options: Option[];
  groupId: MetricGroupId;
};

const METRIC_GROUPS: ReadonlyArray<{
  id: Exclude<MetricGroupId, 'other'>;
  metrics: readonly MachineConfigUIMetricEnum[];
  labelKey: string;
}> = [
  {
    id: 'time',
    labelKey: 'time',
    metrics: [
      'uptime',
      'downtime',
      'plannedDowntime',
      'unplannedDowntime',
      'stops',
      'annotation'
    ]
  },
  {
    id: 'availability',
    labelKey: 'availability',
    metrics: ['availability', 'netAvailability']
  },
  {
    id: 'products',
    labelKey: 'products',
    metrics: ['total', 'correct', 'avg_velocity']
  },
  {
    id: 'performance',
    labelKey: 'performance',
    metrics: [
      'oee_effectiveness',
      'total_performance',
      'idealProduction',
      'idealSpeedProduction'
    ]
  },
  {
    id: 'quality',
    labelKey: 'quality',
    metrics: ['defects', 'oee_quality']
  },
  {
    id: 'oee',
    labelKey: 'realtime_settings_metric_group_oee',
    metrics: ['oee_oee', 'netOee', 'total_oee']
  }
];

export type MetricGroupLegendItem = {
  id: MetricGroupId;
  labelKey: string;
};

/** Ordered legend entries for Realtime Settings drag-and-drop color key. */
export const METRIC_GROUP_LEGEND_ITEMS: ReadonlyArray<MetricGroupLegendItem> = [
  ...METRIC_GROUPS.map((g) => ({ id: g.id, labelKey: g.labelKey })),
  { id: 'other', labelKey: 'other' }
];

const FORCE_METRICS_SORT: Readonly<MachineConfigUIMetricEnum[]> =
  METRIC_GROUPS.flatMap((g) => [...g.metrics]);

const METRIC_TO_GROUP: ReadonlyMap<MachineConfigUIMetricEnum, MetricGroupId> =
  new Map(
    METRIC_GROUPS.flatMap((g) => g.metrics.map((m) => [m, g.id] as const))
  );

export const getMetricGroup = (
  metric: MachineConfigUIMetricEnum
): MetricGroupId => METRIC_TO_GROUP.get(metric) ?? 'other';

export const getMetricGroupBadgeClassName = (group: MetricGroupId): string => {
  // Soft fills — distinct categorical hues (ui-ux-pro-max: industrial slate +
  // stock green; avoid status yellow/red used for stopped/alert elsewhere).
  switch (group) {
    case 'time':
      return 'tw-bg-sky-100 tw-ring-sky-200';
    case 'availability':
      return 'tw-bg-teal-100 tw-ring-teal-200';
    case 'products':
      return 'tw-bg-emerald-100 tw-ring-emerald-200';
    case 'performance':
      return 'tw-bg-indigo-100 tw-ring-indigo-200';
    case 'quality':
      return 'tw-bg-violet-100 tw-ring-violet-200';
    case 'oee':
      return 'tw-bg-slate-200 tw-ring-slate-300';
    case 'other':
    default:
      return 'tw-bg-gray-100 tw-ring-gray-200';
  }
};

export const getMetricGroupHeadingTextClassName = (
  group: MetricGroupId
): string => {
  // Darker on-tone text for ≥4.5:1 contrast on light surfaces.
  switch (group) {
    case 'time':
      return 'tw-text-sky-800';
    case 'availability':
      return 'tw-text-teal-800';
    case 'products':
      return 'tw-text-emerald-800';
    case 'performance':
      return 'tw-text-indigo-800';
    case 'quality':
      return 'tw-text-violet-800';
    case 'oee':
      return 'tw-text-slate-800';
    case 'other':
    default:
      return 'tw-text-gray-700';
  }
};

export const getMetricGroupDividerClassName = (
  group: MetricGroupId
): string => {
  switch (group) {
    case 'time':
      return 'tw-bg-sky-200';
    case 'availability':
      return 'tw-bg-teal-200';
    case 'products':
      return 'tw-bg-emerald-200';
    case 'performance':
      return 'tw-bg-indigo-200';
    case 'quality':
      return 'tw-bg-violet-200';
    case 'oee':
      return 'tw-bg-slate-200';
    case 'other':
    default:
      return 'tw-bg-gray-200';
  }
};

export const compareMetricsForSort = (m1: UIMetric, m2: UIMetric) => {
  const indexA = FORCE_METRICS_SORT.indexOf(m1.metric);
  const indexB = FORCE_METRICS_SORT.indexOf(m2.metric);
  return (
    (indexA === -1 ? FORCE_METRICS_SORT.length + m1.order : indexA) -
    (indexB === -1 ? FORCE_METRICS_SORT.length + m2.order : indexB)
  );
};

export const getMetricDisplayName = (metricKey: MachineConfigUIMetricEnum) => {
  return I18n.t(`machine_metric_${metricKey}`);
};

export const metricsAsOptions = (metrics: UIMetric[]): Option[] => {
  return metrics
    .filter((m) => !isUnorderedMetric(m)) // remove hour by hour metrics
    .sort(compareMetricsForSort)
    .map((metric: UIMetric) => ({
      label: getMetricDisplayName(metric.metric),
      value: metric.metric
    }));
};

export const metricsAsGroupedOptions = (
  metrics: UIMetric[]
): MetricOptionGroup[] => {
  const sorted = metrics
    .filter((m) => !isUnorderedMetric(m))
    .sort(compareMetricsForSort);

  const byGroup = new Map<MetricGroupId, Option[]>();
  for (const m of sorted) {
    const group = getMetricGroup(m.metric);
    const list = byGroup.get(group) ?? [];
    list.push({
      label: getMetricDisplayName(m.metric),
      value: m.metric
    });
    byGroup.set(group, list);
  }

  const groups: MetricOptionGroup[] = [];
  for (const def of METRIC_GROUPS) {
    const options = byGroup.get(def.id);
    if (options?.length) {
      groups.push({
        label: I18n.t(def.labelKey),
        options,
        groupId: def.id
      });
    }
  }
  const other = byGroup.get('other');
  if (other?.length) {
    groups.push({
      label: I18n.t('other'),
      options: other,
      groupId: 'other'
    });
  }
  return groups;
};

export const subtractMetrics = (
  metrics: UIMetric[],
  removeMetrics: UIMetric[]
) => {
  return metrics.filter(
    (m) => !removeMetrics.some((rm) => rm.metric === m.metric)
  );
};

const VELOCITY_METRIC_NAMES: ReadonlyArray<MachineConfigUIMetricEnum> = [
  'sensors_instantaneous_velocity',
  'instantaneous_velocity',
  'sensors_avg_velocity',
  'avg_velocity'
];

export const isVelocityMetricName = (
  name: MachineConfigUIMetricEnum
): boolean => VELOCITY_METRIC_NAMES.includes(name);

const DEFAULT_VIEW_BY_PARAM: MachineConfigUIParameter = {
  name: 'view_by',
  options: ['min', 'h'],
  type: 'option',
  value: 'min'
};

/**
 * Injects the default `view_by` parameter for velocity metrics if not already present.
 * Preserves any previously saved value (e.g. 'h' for hours).
 */
export const withDefaultViewByParam = (metric: UIMetric): UIMetric => {
  if (!isVelocityMetricName(metric.metric)) return metric;
  if (metric.parameters?.some((p) => p.name === 'view_by')) return metric;
  return {
    ...metric,
    parameters: [...(metric.parameters ?? []), DEFAULT_VIEW_BY_PARAM]
  };
};

export const MACHINE_CONFIG_UI_PRODUCTION_TYPES: readonly MachineConfigUIProductionTypeEnum[] =
  ['total', 'correct', 'defects'];

export const isMachineConfigUIProductionType = (
  metric: MachineConfigUIMetricEnum
): metric is MachineConfigUIProductionTypeEnum =>
  (MACHINE_CONFIG_UI_PRODUCTION_TYPES as readonly string[]).includes(metric);
